import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, MapPinned, ShieldCheck, CheckCircle2 } from "lucide-react";
import { API, errMsg, dashboardFor } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const FIELD = "w-full border border-stone-300 bg-white rounded-lg px-4 py-3 text-sm outline-none focus:border-[#6A1B29] transition-colors";

const CARDS = [
  { key: "customer", icon: ShoppingBag, title: "Customer", desc: "Shop cosmetics, fashion, jewellery and accessories.", note: "Start shopping in minutes" },
  { key: "outlet", icon: MapPinned, title: "Outlet Partner", desc: "Explore and apply for your own Suvikka outlet.", note: "Investment from ₹3,00,000*" },
  { key: "admin", icon: ShieldCheck, title: "Admin", desc: "Authorized administration access.", note: "Restricted" },
];

export default function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("customer");
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [f, setF] = useState({ name: "", business_name: "", mobile: "", email: "", password: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) { toast.error("Please accept the Terms & Privacy Policy"); return; }
    setBusy(true);
    try {
      const apiRole = role === "outlet" ? "retail_owner" : role;
      const { data } = await API.post("/auth/register", { name: f.name, email: f.email, password: f.password, mobile: f.mobile, business_name: f.business_name, role: apiRole, referral: localStorage.getItem("svk_ref") || "" });
      setUser(data.user);
      toast.success("Account created");
      if (role === "outlet") nav("/open-outlet");
      else nav(dashboardFor(data.user.role));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white min-h-[70vh]" data-testid="signup-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6A1B29] text-center">Join Suvikka</p>
        <h1 className="font-display text-3xl sm:text-5xl font-semibold text-stone-900 text-center mt-3">Create Your Suvikka Account</h1>
        <p className="text-stone-500 text-center mt-3">Choose your account type to get started. Already have one? <Link to="/login" className="text-[#6A1B29] font-medium" data-testid="signup-login-link">Login</Link></p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="signup-role-cards">
          {CARDS.map(({ key, icon: Icon, title, desc, note }) => (
            <button key={key} onClick={() => setRole(key)} data-testid={`signup-card-${key}`}
              className={`text-left rounded-2xl border-2 p-5 transition-all ${role === key ? "border-[#6A1B29] bg-[#FFF5F8] shadow-md" : "border-stone-200 bg-white hover:border-stone-300"}`}>
              <span className={`inline-flex p-2.5 rounded-xl ${role === key ? "bg-[#6A1B29] text-white" : "bg-[#FFF0F4] text-[#6A1B29]"}`}><Icon className="w-5 h-5" /></span>
              <p className="font-display text-lg font-semibold text-stone-900 mt-3">{title}</p>
              <p className="text-sm text-stone-500 mt-1">{desc}</p>
              <p className="text-xs font-medium text-[#6A1B29] mt-3 inline-flex items-center gap-1">{role === key && <CheckCircle2 className="w-3.5 h-3.5" />}{note}</p>
            </button>
          ))}
        </div>

        {role === "admin" ? (
          <div className="mt-8 max-w-xl mx-auto bg-stone-50 border border-stone-200 rounded-2xl p-8 text-center" data-testid="signup-admin-restricted">
            <ShieldCheck className="w-10 h-10 text-stone-400 mx-auto" />
            <h2 className="font-display text-xl font-semibold text-stone-900 mt-4">Admin access is restricted</h2>
            <p className="text-sm text-stone-500 mt-2">Admin accounts are created and managed internally by Suvikka. If you are an authorised team member, use the admin login.</p>
            <Link to="/admin/login" className="inline-block mt-5 bg-stone-900 text-white text-sm font-semibold px-6 py-3 rounded-full" data-testid="signup-admin-login-link">Go to Admin Login</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 max-w-xl mx-auto bg-white border border-stone-200 rounded-2xl p-6 sm:p-8" data-testid="signup-form">
            <h2 className="font-display text-xl font-semibold text-stone-900 mb-5" data-testid="signup-form-title">
              {role === "customer" && "Customer Registration"}
              {role === "outlet" && "Outlet Partner Registration"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Full Name *" value={f.name} onChange={set("name")} className={FIELD} data-testid="signup-name" />
              <input required placeholder="Mobile Number *" value={f.mobile} onChange={set("mobile")} className={FIELD} data-testid="signup-mobile" />
              <input required type="email" placeholder="Email *" value={f.email} onChange={set("email")} className={FIELD} data-testid="signup-email" />
              <input required type="password" minLength={6} placeholder="Create Password (min 6 chars) *" value={f.password} onChange={set("password")} className={`${FIELD} sm:col-span-2`} data-testid="signup-password" />
            </div>
            <label className="flex items-start gap-2.5 mt-5 text-sm text-stone-600 cursor-pointer">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 accent-[#6A1B29]" data-testid="signup-terms" />
              <span>I agree to the Suvikka Terms &amp; Conditions and Privacy Policy.{role === "outlet" && " I understand outlet activation requires application review and payment confirmation."}</span>
            </label>
            <button disabled={busy} data-testid="signup-submit"
              className="w-full mt-6 bg-[#6A1B29] hover:bg-[#501325] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors">
              {busy ? "Please wait…" : role === "outlet" ? "CREATE ACCOUNT & EXPLORE OUTLET OPPORTUNITY" : "CREATE ACCOUNT"}
            </button>
            {role === "outlet" && <p className="text-xs text-stone-400 mt-3 text-center">Next: explore the outlet opportunity, apply, and complete payment. Your partner dashboard activates after admin payment confirmation.</p>}
          </form>
        )}
      </div>
    </div>
  );
}
